import * as S from './ProductBleed.styled'
import { useRecoilValue } from 'recoil'
import { showProductBleedAtom } from 'atoms'
import useColors from 'hooks/utility/useColors'
import type { FoldMargin } from '@messenger/utils'

type ProductBleedProps = {
  height: number
  width: number
  marginTop?: number | null
  marginRight?: number | null
  marginBottom?: number | null
  marginLeft?: number | null
  marginCenterHorizontal?: number | null
  marginCenterVertical?: number | null
  showVerticalCenter?: boolean
  showHorizontalCenter?: boolean
  /** A trifold page's fold margins, in px from the sheet's left edge. */
  foldMargins?: FoldMargin[] | null
  hide?: boolean
}

/**
 * Renders cyan margin overlays around product edges as a buffer area to help production facilities by preventing users from placing assets too close to paper edges during printing
 */
export const ProductBleed = ({
  height,
  width,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  marginCenterHorizontal,
  marginCenterVertical,
  showVerticalCenter = false,
  showHorizontalCenter = false,
  foldMargins,
  hide = false,
}: ProductBleedProps) => {
  const showProductBleed = useRecoilValue(showProductBleedAtom)
  const getColor = useColors()

  const hideProductBleedOverlay = hide || !showProductBleed
  if (hideProductBleedOverlay) return null

  // Only a positive margin draws a bleed frame. A margin of 0 / null / undefined
  // means "no bleed buffer on that edge" and must render nothing. Guarding with the
  // raw value (e.g. `{marginTop && <Frame/>}`) leaks the number 0 as visible text
  // ("00000000") because 0 is falsy but is still a valid React child.
  const top = marginTop ?? 0
  const right = marginRight ?? 0
  const bottom = marginBottom ?? 0
  const left = marginLeft ?? 0
  const centerVertical = marginCenterVertical ?? 0
  const centerHorizontal = marginCenterHorizontal ?? 0
  const drawnFoldMargins = (foldMargins ?? []).filter(
    (foldMargin) => foldMargin.width > 0,
  )

  const hasAnyMargin =
    drawnFoldMargins.length > 0 ||
    top > 0 ||
    right > 0 ||
    bottom > 0 ||
    left > 0 ||
    centerVertical > 0 ||
    centerHorizontal > 0
  if (!hasAnyMargin) return null

  const color = getColor('productBleedBackground', 45)

  return (
    <S.Container height={height} width={width}>
      {top > 0 && (
        <S.TopFrame
          color={color}
          topMargin={top}
          leftMargin={left}
          rightMargin={right}
        />
      )}
      {right > 0 && (
        <S.RightFrame
          color={color}
          rightMargin={right}
          topMargin={top}
          bottomMargin={bottom}
        />
      )}
      {bottom > 0 && (
        <S.BottomFrame
          color={color}
          bottomMargin={bottom}
          leftMargin={left}
          rightMargin={right}
        />
      )}
      {left > 0 && (
        <S.LeftFrame
          color={color}
          leftMargin={left}
          topMargin={top}
          bottomMargin={bottom}
        />
      )}
      {top > 0 && left > 0 && (
        <S.TopLeftCorner
          color={color}
          thicknessTop={top}
          thicknessLeft={left}
        />
      )}
      {top > 0 && right > 0 && (
        <S.TopRightCorner
          color={color}
          thicknessTop={top}
          thicknessRight={right}
        />
      )}
      {bottom > 0 && left > 0 && (
        <S.BottomLeftCorner
          color={color}
          thicknessBottom={bottom}
          thicknessLeft={left}
        />
      )}
      {bottom > 0 && right > 0 && (
        <S.BottomRightCorner
          color={color}
          thicknessBottom={bottom}
          thicknessRight={right}
        />
      )}
      {(showVerticalCenter || centerVertical > 0) && centerVertical > 0 && (
        <S.VerticalCenterFrame
          color={color}
          thickness={centerVertical}
          topMargin={top}
          bottomMargin={bottom}
        />
      )}
      {(showHorizontalCenter || centerHorizontal > 0) &&
        centerHorizontal > 0 && (
          <S.HorizontalCenterFrame
            color={color}
            thickness={centerHorizontal}
            leftMargin={left}
            rightMargin={right}
          />
        )}
      {drawnFoldMargins.map((foldMargin) => (
        <S.FoldFrame
          key={foldMargin.center}
          color={color}
          center={foldMargin.center}
          thickness={foldMargin.width}
          topMargin={top}
          bottomMargin={bottom}
        />
      ))}
    </S.Container>
  )
}
